import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

interface Props {
  open: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  successMessage?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

export default function UnsubscribeConfirmDialog({
  open,
  isSubmitting,
  errorMessage,
  successMessage,
  onConfirm,
  onClose,
}: Props) {
  const dialogId = useId();
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    confirmButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSubmitting) onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, isSubmitting, onClose]);

  if (!open) return null;

  const isSuccess = Boolean(successMessage);

  return createPortal(
    <div
      className="fixed inset-0 z-[10060] flex items-center justify-center bg-[#003f28]/35 p-4"
      onClick={() => {
        if (!isSubmitting) onClose();
      }}
    >
      <div
        id={dialogId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${dialogId}-title`}
        aria-describedby={`${dialogId}-description`}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2
          id={`${dialogId}-title`}
          className="m-0 text-xl font-semibold leading-tight text-[#003f28]"
        >
          {isSuccess ? "Solicitud registrada" : "¿Confirmas que quieres tramitar la baja?"}
        </h2>

        <p
          id={`${dialogId}-description`}
          className="mt-3 text-sm leading-relaxed text-[#003f28]/80"
        >
          {isSuccess
            ? successMessage
            : "La solicitud quedará registrada y debe ser aprobada. El proceso puede tardar unos días en tramitarse."}
        </p>

        {errorMessage ? (
          <p className="mt-3 text-sm text-red-700" role="alert">
            {errorMessage}
          </p>
        ) : null}

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          {isSuccess ? (
            <button
              type="button"
              className="rounded-full bg-[#ffa61c] px-5 py-2.5 text-sm font-semibold text-[#003f28] transition hover:bg-[#ff9a00] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0ab2fe]"
              onClick={onClose}
            >
              Entendido
            </button>
          ) : (
            <>
              <button
                type="button"
                className="rounded-full border border-[#003f28]/20 px-5 py-2.5 text-sm font-medium text-[#003f28] transition hover:bg-[#003f28]/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0ab2fe] disabled:cursor-not-allowed disabled:opacity-60"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                ref={confirmButtonRef}
                type="button"
                className="rounded-full bg-[#ffa61c] px-5 py-2.5 text-sm font-semibold text-[#003f28] transition hover:bg-[#ff9a00] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0ab2fe] disabled:cursor-not-allowed disabled:opacity-60"
                onClick={onConfirm}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Registrando…" : "Tramitar baja"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
